import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { linkButtonClassName, linkButtonProps } from '../link-button.utils';
import { sprinkles } from '@/css/sprinkles.css';
import clsx from 'clsx';
import { extractProps } from '@/utils/vanilla-extract-props';
import PlumaBox from '../../box/ember/index';
import type { BoxSignature } from '../../box/ember/index';
import PlumaIcon from '../../icon/ember/index';
import type { BaseLinkButtonProps } from '../link-button.utils';
import { consume } from 'ember-provide-consume-context';
import type ContextRegistry from 'ember-provide-consume-context/context-registry';
import { PlumaContext } from '../../pluma-provider/ember/index';
// @ts-ignore
import { on } from '@ember/modifier';
export interface LinkButtonSignature<T extends string = 'button'> {
    Element: BoxSignature<T>['Element'];
    Args: BoxSignature<T>['Args'] & BaseLinkButtonProps;
    Blocks: {
        default: [];
    };
}
export default class LinkButton<T extends string = 'button'> extends Component<LinkButtonSignature<T>> {
    @consume(PlumaContext)
    plumaContext: ContextRegistry[typeof PlumaContext];
    get as() {
        return (this.args.as ?? 'button') as any;
    }
    get className() {
        // Until something like https://github.com/emberjs/rfcs/pull/593 lands in
        // Ember, we can't forward arbitrary args to the PlumaBox component.
        // Instead, we need to extract sprinkle arguments and get the class names in
        // each Ember component.
        const { recipeProps: recipeProps1, sprinkleProps: sprinkleProps1 } = extractProps(this.args, linkButtonProps);
        recipeProps1['unsafe_isLegacy'] = recipeProps1['unsafe_isLegacy'] ?? this.plumaContext?.componentFlags?.unsafe_useLegacyButton ?? false;
        return clsx(linkButtonClassName(recipeProps1), sprinkles(sprinkleProps1));
    }
    get iconPosition() {
        return this.args.iconPosition ?? 'leading';
    }
    get hasLeadingIcon() {
        return this.args.icon != null && this.iconPosition === 'leading';
    }
    get hasTrailingIcon() {
        return this.args.icon != null && this.iconPosition === 'trailing';
    }
    get iconAlignment() {
        const { iconPosition: iconPosition1 } = this;
        let iconAlignment1: 'uppercase' | 'lowercase' = 'uppercase';
        if (iconPosition1 === 'trailing') {
            iconAlignment1 = 'lowercase';
        }
        if (this.args.iconAlignment != null) {
            iconAlignment1 = this.args.iconAlignment;
        }
        return iconAlignment1;
    }
    get iconSize() {
        return this.args.iconSize ?? 'md';
    }
    get isDisabled() {
        return this.args.isDisabled ?? false;
    }
    get disabledAttr() {
        return this.isDisabled === true ? true : undefined;
    }
    get type() {
        if (this.as !== 'button') {
            return undefined;
        }
        return 'button';
    }
    get tabIndex() {
        return this.isDisabled ? -1 : undefined;
    }
    maybeBlockClick = (e1: MouseEvent)=>{
        if (this.isDisabled) {
            e1.preventDefault();
            e1.stopPropagation();
        }
    };
    static{
        template(`<PlumaBox
		@as={{this.as}}
		class={{this.className}}
		type={{this.type}}
		{{on "click" this.maybeBlockClick capture=true}}
		tabindex={{this.tabIndex}}
		{{! @glint-expect-error }}
		...attributes
		disabled={{this.disabledAttr}}
		aria-disabled={{this.disabledAttr}}
	>
		{{~#if this.hasLeadingIcon~}}
			<PlumaIcon
				{{! @glint-expect-error }}
				@name={{@icon}}
				@isInline={{true}}
				@inlineAlignment={{this.iconAlignment}}
				@size={{this.iconSize}}
				@mr="050"
			/>
		{{~/if~}}
		{{~yield~}}
		{{~#if this.hasTrailingIcon~}}
			<PlumaIcon
				{{! @glint-expect-error }}
				@name={{@icon}}
				@isInline={{true}}
				@inlineAlignment={{this.iconAlignment}}
				@size={{this.iconSize}}
				@ml="050"
			/>
		{{~/if~}}
	</PlumaBox>`, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
